import { IonButton, IonItem, IonLabel, IonText, IonToggle, ToggleChangeEventDetail } from '@ionic/react';
import { IonToggleCustomEvent } from '@ionic/core';
import { isPlatform } from '../utils/isPlatform';
import { FC, useEffect, useState } from 'react';
import { createUseStyles } from 'react-jss';
import { useInstance } from '@meraki-internal/react-dependency-injection';
import { CareMapState } from '../caremap/CareMapState';
import { useSubscription } from '@meraki-internal/state';
import { ISupportGroupKey } from '../caremap/ICareMap';
import { HistoryViewModel } from '../support/HistoryViewModel';
import { CareMapDrawerPage } from '../components/CareMapDrawerPage';
import { DrawerSectionHeading } from '../components/DrawerSectionHeading';
import { DeleteSupportGroupButton } from './DeleteSupportGroupButton';
import { SupportGroupIconProvider } from '../caremap/SupportGroupIconProvider';
import { ShortContactMethodsList } from '../contact-methods/ShortContactMethodsList';
import { CareMapNotesList } from '../notes/note-list/CareMapNotesList';
import { NudgeBadge } from '../components/NudgeBadge';
import { useBreakpoints } from '../theme/useBreakpoints';
import { DrawerListSection } from '../components/DrawerListSection';
import { AppointmentsList } from '../appointments/AppointmentsList';
import { AppointmentsState, NEW_APPOINTMENT_ID } from '../appointments/AppointmentsState';
import { Checklists } from '../checklists/Checklists';
import { HelpCenterState } from '../support/help-center/HelpCenterState';
import { HelpCenterArticleList } from '../support/help-center/HelpCenterArticleList';
import { AsyncStorageProvider } from '../support/AsyncStorageProvider';
import { useAsyncEffect } from '../support/useAsyncEffect';
import { CaremapNotesModelFactory } from '../notes/model/CaremapNotesModelFactory';
import { AddFromPopover } from '../contacts/AddFromPopover';

const useStyles = createUseStyles({
    iconContainer: {
        marginTop: 10,
        marginBottom: 10
    },

    memberLabel: {
        lineHeight: '1.3em'
    },

    memberTitle: {
        fontWeight: 500
    },

    memberInfo: {
        display: 'block',
        fontSize: '0.95em',
        color: 'var(--ion-color-step-750)'
    }
});

export interface ISupportGroupMemberList {
    supportGroupKey: ISupportGroupKey
};

// currently doesn't use IonList even though List is in the name
export const SupportGroupMemberList: FC<ISupportGroupMemberList> = ({ supportGroupKey }) => {
    const classes = useStyles();

    const careMapStateInstance = useInstance(CareMapState);
    useSubscription(() => careMapStateInstance);

    const notesFactory = useInstance(CaremapNotesModelFactory);
    useSubscription(() => notesFactory);

    const iconProvider = useInstance(SupportGroupIconProvider);

    const history = useInstance(HistoryViewModel);
    const appointmentsState = useInstance(AppointmentsState);

    const helpCenterState = useInstance(HelpCenterState);
    useSubscription(() => helpCenterState);

    const storageProvider = useInstance(AsyncStorageProvider);

    const {isBelowBreakpoint} = useBreakpoints();

    const [showAddMemberPopover, setShowAddMemberPopover] = useState<boolean>(false);
    const [hideDoneChecklistItems, setHideDoneChecklistItems] = useState<boolean>(false);

    useEffect(() => {
        helpCenterState.load();
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    const hideDoneStorageKey = `${supportGroupKey}-hide-done-checklists`;
    useAsyncEffect(async () => {
        const hideDone = await storageProvider.getBooleanProvider(hideDoneStorageKey).get();
        setHideDoneChecklistItems(hideDone || false);
    }, [storageProvider]);

    const navigateToAdd = () => {
        history.push(`/caremaps/${careMapStateInstance.getActiveCaremap()?.careMapId}/drawer/support-group/${supportGroupKey}/add-member`);
    };

    const onAdd = () => {
        if (isPlatform('capacitor')) {
            setShowAddMemberPopover(true);
        } else {
            navigateToAdd();
        }

    };

    const viewMoreNotes = () => {
        history.push(`/caremaps/${careMapStateInstance.getActiveCaremap()?.careMapId}/drawer/support-group/${supportGroupKey}/notes`);
    };

    const editNote = (noteId: string) => {
        history.push(`/caremaps/${careMapStateInstance.getActiveCaremap()?.careMapId}/drawer/support-group/${supportGroupKey}/notes/${noteId}`);
    };

    const addNote = () => {
        history.push(`/caremaps/${careMapStateInstance.getActiveCaremap()?.careMapId}/drawer/support-group/${supportGroupKey}/notes/add-note`);
    };

    const viewMoreAppointments = () => {
        history.push(`${history.getCurrentLocation().pathname}/appointments`);
    };

    const editAppointment = (appointmentId?: string) => {
        if (appointmentId === NEW_APPOINTMENT_ID) {
            appointmentsState.trackNewAppointmentOpened({ supportGroupKey });
        }

        history.push(`${history.getCurrentLocation().pathname}/appointments/${appointmentId}`);
    };

    const viewMoreArticles = () => {
        history.push(`${history.getCurrentLocation().pathname}/help-center/articles`);
    };

    const labels = careMapStateInstance.getSupportGroupLabels(supportGroupKey);
    const color = careMapStateInstance.getSupportGroupColor(supportGroupKey);
    const { MemberIcon } = iconProvider.getSupportGroupIcons(supportGroupKey);


    const canAdd = (careMapStateInstance.getActiveCaremap()?.links.self.actions.includes('add-support'));
    const canDelete = careMapStateInstance.canDeleteSupportGroup(supportGroupKey);

    const buttons = <>
        {canAdd && (
            <IonButton id="add-member-btn" data-type="add-member-button" onClick={onAdd}>
                + Add {canDelete && isBelowBreakpoint('sm') ? labels.shortMemberTitle : labels.memberTitle}
            </IonButton>
        )}
        {canDelete && (
            <DeleteSupportGroupButton supportGroupKey={supportGroupKey} />
        )}
    </>;

    const listShowCount = 2;
    const members = careMapStateInstance.getSupportGroupMembers(supportGroupKey);
    const showNameFirst = careMapStateInstance.shouldShowNameFirst(supportGroupKey);

    const checkLists = careMapStateInstance.getChecklists(supportGroupKey);
    const hasChecklists = checkLists.length > 0;

    const collections = helpCenterState.getCollectionsAndArticlesForCategory(supportGroupKey);
    const hasArticles = helpCenterState.hasArticles(collections);

    const notes = notesFactory.forActiveCaremap().getNotesForTarget({ section: supportGroupKey });

    return (
        <CareMapDrawerPage title={labels.title} buttons={buttons}>

            {hasChecklists && (
                <DrawerListSection underDrawerTitle>
                    <DrawerSectionHeading text="Checklist">
                        <IonToggle
                            style={{fontSize: 14}}
                            data-type="hide-done-toggle"
                            checked={hideDoneChecklistItems}
                            onIonChange={(e: IonToggleCustomEvent<ToggleChangeEventDetail<any>>) => {
                                setHideDoneChecklistItems(e.detail.checked);
                                storageProvider.getBooleanProvider(hideDoneStorageKey).set(e.detail.checked);
                            }}
                        >
                            Hide Completed
                        </IonToggle>
                    </DrawerSectionHeading>
                    <Checklists
                        checklists={checkLists}
                        hideDone={hideDoneChecklistItems}
                        supportGroupKey={supportGroupKey}
                    />
                </DrawerListSection>
            )}

            <DrawerListSection underDrawerTitle={!hasChecklists && !hasArticles}>
                <CareMapNotesList
                    showCount={listShowCount}
                    onNoteClicked={editNote}
                    onAddNote={addNote}
                    noteTarget={{ section: supportGroupKey }}
                    moreNotesHandler={viewMoreNotes}
                    notes={notes}
                />
            </DrawerListSection>

            <DrawerListSection>
                <AppointmentsList
                    showViewMore
                    numDaysToShow={1}
                    ignoreHasScrolled
                    showCount={listShowCount}
                    minDate={(new Date()).toISOString()}
                    supportGroupKey={supportGroupKey}
                    editAppointment={editAppointment}
                    viewMore={viewMoreAppointments}
                    data-type="appointments-list"
                />
            </DrawerListSection>

            {hasArticles && (
                <DrawerListSection data-id="articles-section">
                    <DrawerSectionHeading text="Help Articles" />
                    <HelpCenterArticleList
                        collections={collections}
                        viewMore={viewMoreArticles}
                        numCollectionsToShow={1}
                        numArticlesToShow={2}
                        supportGroupKey={supportGroupKey}
                    />
                </DrawerListSection>
            )}

            <div data-type="member-list">
                <div style={{paddingInlineStart: 5}}>
                    <DrawerSectionHeading text={`${labels.memberTitle}s`} />
                </div>
                <div>
                    {!members.length && careMapStateInstance.hasPermissionTo('put') &&
                        <NudgeBadge
                            buttonClickHandler={onAdd}
                            message={`Enhance your care map by adding ${labels.memberTitle.toLowerCase()}s`}
                            buttonLabel={`Add ${canDelete && isBelowBreakpoint('sm') ? labels.shortMemberTitle : labels.memberTitle}`}
                        />
                    }
                    {members.map(member => {
                        const title = (showNameFirst ? member.name : member.role);
                        const subtitle = (showNameFirst ? member.role : member.name);
                        const isMe = careMapStateInstance.isMySupportMember(member);

                        return (
                            <IonItem
                                button
                                detail
                                key={member.id}
                                onClick={() => history.push(`/caremaps/${careMapStateInstance.getActiveCaremap()?.careMapId}/drawer/support-group/${supportGroupKey}/member/${member.id}`)}
                            >
                                <div slot="start" className={classes.iconContainer}>
                                    <MemberIcon size="xlarge" shape="round" color={color} />
                                </div>
                                <IonLabel className={classes.memberLabel}>
                                    <div>
                                        <div data-type="title" className={classes.memberTitle}>{title || ''}{isMe ? <span style={{ fontWeight: 'normal' }}> (you)</span> : ''}</div>
                                        <IonText data-type="detail1" className={classes.memberInfo}>{subtitle || ''}</IonText>
                                        <IonText data-type="detail2" className={classes.memberInfo}>{member.organization}</IonText>
                                    </div>
                                    <ShortContactMethodsList
                                        contactMethods={member.contactMethods}
                                        address={member.address}
                                    />
                                </IonLabel>
                            </IonItem>
                        );
                    })}
                </div>
            </div>
            <AddFromPopover
                trigger="add-member-btn"
                isOpen={showAddMemberPopover}
                onDidDismiss={() => setShowAddMemberPopover(false)}
                buttonHandlers={{
                    manually: navigateToAdd,
                    fromContacts: () =>history.push(`/caremaps/${careMapStateInstance.getActiveCaremap()?.careMapId}/drawer/support-group/${supportGroupKey}/add-member?aafc=true`)
                }}
            />
        </CareMapDrawerPage>
    );
};
